// Reusable address-validation UI block. Sits below the 4 structured
// address fields (street / city / state / postal_code) wherever
// addresses are entered. Click "Validate" to run the active provider;
// if the result has a suggestion, render an "Apply suggestion" button
// that calls the consumer's onApply callback with the normalized
// address.
//
// Advisory only -- never blocks submission. The consumer decides
// whether to gate save on result.status (none of the v1 consumers do).
//
// Styling intentionally minimal -- piggybacks the inputClass / button
// classes from logisticsRoutesShared via consumer-provided className
// strings where helpful, but the affordance owns its own small badge
// palette so it doesn't depend on any one bucket's shared module.
import { useAddressValidation } from "./useAddressValidation";
import type {
  AddressValidationStatus,
  StructuredAddress,
} from "@/lib/addressValidationClient";
import { formatStructuredAddressOneLine } from "@/lib/addressValidation/addressFormat";

// Status -> Tailwind class for the badge + container border. Mirrors
// the status semantics in addressValidation/types.ts.
const STATUS_BADGE_CLASS: Record<AddressValidationStatus, string> = {
  ok: "bg-emerald-600 text-white",
  needs_review: "bg-amber-600 text-white",
  incomplete: "bg-gray-400 text-white",
  invalid: "bg-red-600 text-white",
  skipped: "bg-gray-400 text-white",
};

const STATUS_LABEL: Record<AddressValidationStatus, string> = {
  ok: "OK",
  needs_review: "Needs review",
  incomplete: "Incomplete",
  invalid: "Invalid",
  skipped: "Skipped",
};

const STATUS_CONTAINER_CLASS: Record<AddressValidationStatus, string> = {
  ok: "border-emerald-200 bg-emerald-50/40 dark:border-emerald-800 dark:bg-emerald-950/20",
  needs_review:
    "border-amber-200 bg-amber-50/40 dark:border-amber-800 dark:bg-amber-950/20",
  incomplete:
    "border-gray-200 bg-gray-50/40 dark:border-zinc-700 dark:bg-zinc-900/20",
  invalid: "border-red-200 bg-red-50/40 dark:border-red-800 dark:bg-red-950/20",
  skipped: "border-gray-200 bg-gray-50/40 dark:border-zinc-700 dark:bg-zinc-900/20",
};

const validateBtnClass =
  "rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-800 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:bg-indigo-900/50";
const applyBtnClass =
  "rounded-lg bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";
const dismissBtnClass =
  "rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700";

type Props = {
  /** Live address from the form. The affordance reads this on Validate
   * click; not auto-fired on each change. */
  value: StructuredAddress;
  /** Called when the user clicks "Apply suggestion" on a needs_review
   * or invalid result. Receives the provider's normalized form. */
  onApply: (normalized: StructuredAddress) => void;
};

export function AddressValidationAffordance({ value, onApply }: Props) {
  const v = useAddressValidation();
  const result = v.result;
  return (
    <div className="mt-1 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={validateBtnClass}
          onClick={() => void v.run(value)}
          disabled={v.busy}
        >
          {v.busy ? "Validating…" : "Validate address"}
        </button>
        {result ? (
          <>
            <span
              className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_BADGE_CLASS[result.status]}`}
              title={`via ${result.provider}`}
            >
              {STATUS_LABEL[result.status]}
            </span>
            <button type="button" className={dismissBtnClass} onClick={v.clear}>
              Dismiss
            </button>
          </>
        ) : null}
      </div>
      {result ? (
        <div
          className={`rounded-xl border p-3 text-xs space-y-2 ${STATUS_CONTAINER_CLASS[result.status]}`}
        >
          {result.suggestedChange ? (
            <div className="flex flex-wrap items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 dark:text-zinc-100">
                  Suggested:
                </div>
                <div className="text-gray-700 dark:text-zinc-300">
                  {formatAddressOneLine(result.normalized)}
                </div>
              </div>
              <button
                type="button"
                className={applyBtnClass}
                onClick={() => {
                  onApply(result.normalized);
                  v.clear();
                }}
              >
                Apply suggestion
              </button>
            </div>
          ) : null}
          {result.issues.length > 0 ? (
            <ul className="space-y-1">
              {result.issues.map((iss, idx) => (
                <li
                  key={idx}
                  className={
                    iss.severity === "error"
                      ? "text-red-800 dark:text-red-300"
                      : "text-amber-800 dark:text-amber-300"
                  }
                >
                  {iss.field ? <code>{iss.field}</code> : "address"}: {iss.message}
                </li>
              ))}
            </ul>
          ) : null}
          {!result.suggestedChange && result.issues.length === 0 && result.status === "ok" ? (
            <div className="text-emerald-800 dark:text-emerald-300">
              Looks good. Safe to submit.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function formatAddressOneLine(a: StructuredAddress): string {
  return formatStructuredAddressOneLine(a) || "(empty)";
}
